import { useState, useContext, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { InboxList, type Conversation } from "./InboxList";
import { ChatWindow } from "./ChatWindow";
import { ContactPanel } from "./ContactPanel";
import { User, ArrowLeft } from "lucide-react";
import { i18n } from "../../../translate/i18n";
import { AuthContext } from "../../../context/Auth/AuthContext";

// Hook para detectar tamanho da tela
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({ width: window.innerWidth });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

export function AtendimentosPage() {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const { user } = useContext(AuthContext) as any;
  const { width } = useWindowSize();

  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    if (isMobile) {
      setShowChatOnMobile(true);
    }
  };

  const handleBackToList = () => {
    setShowChatOnMobile(false);
  };

  // Callback quando ticket é fechado - limpa a conversa selecionada
  const handleTicketClosed = () => {
    setSelectedConversation(null);
    setShowChatOnMobile(false);
  };

  // Formatar data de expiração
  const formatExpirationDate = () => {
    if (user?.company?.dueDate) {
      const date = new Date(user.company.dueDate);
      return date.toLocaleDateString();
    }
    return "";
  };

  return (
    <div className="flex h-full w-full gap-[3px]">
      {/* Left Column: Welcome Banner + InboxList */}
      {/* Mobile: esconde quando chat está aberto */}
      <div
        className={`
          flex flex-col bg-[var(--sidebar-bg)] transition-all duration-300
          ${isMobile
            ? showChatOnMobile
              ? 'hidden'
              : 'w-full'
            : 'w-[360px] flex-shrink-0'
          }
        `}
      >
        {/* Welcome Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-shrink-0 p-3"
        >
          {/* Message inside rounded rectangle */}
          <div className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#1E40AF] to-[#2563EB] px-3 py-2.5 sm:px-4 sm:py-3">
            {/* User Avatar */}
            <div className="relative flex-shrink-0">
              <img
                src={user?.profileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "U")}&background=random`}
                alt={user?.name || "User"}
                className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border-2 border-white/30 object-cover shadow-lg"
              />
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full border-2 border-[#1E40AF] bg-[#22C55E]" />
            </div>

            {/* Welcome Text */}
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-medium text-white/70">
                {i18n.t("v2.welcome.welcomeBackShort")}
              </span>
              <span className="text-sm font-bold text-white truncate">
                {user?.name || "User"} 👋
              </span>
              <span className="text-xs text-white/60 hidden sm:block">
                {i18n.t("v2.welcome.activeUntil", { date: formatExpirationDate(), interpolation: { escapeValue: false } })}
              </span>
            </div>

            {/* Online Badge */}
            <div className="flex-shrink-0">
              <div className="flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-1 sm:px-2.5 sm:py-1.5 backdrop-blur-sm">
                <div className="h-2 w-2 animate-pulse rounded-full bg-[#22C55E]" />
                <span className="text-xs font-medium text-white hidden sm:inline">{i18n.t("v2.welcome.online")}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* InboxList */}
        <div className="flex flex-1 overflow-hidden">
          <InboxList
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
          />
        </div>
      </div>

      {/* Right Column: Chat Window + Contact Panel */}
      {/* Mobile: esconde quando lista está visível, mostra quando chat está aberto */}
      <div
        className={`
          flex flex-1 overflow-hidden
          ${isMobile
            ? showChatOnMobile
              ? 'flex'
              : 'hidden'
            : 'flex'
          }
        `}
      >
        {/* Chat Window */}
        <div className="relative flex flex-1 flex-col overflow-hidden">
          {/* Botão Voltar - Mobile Only */}
          {isMobile && showChatOnMobile && (
            <button
              onClick={handleBackToList}
              className="absolute left-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm transition-all hover:bg-[var(--bg-tertiary)]"
            >
              <ArrowLeft className="h-5 w-5 text-[var(--text-secondary)]" />
            </button>
          )}

          <ChatWindow conversation={selectedConversation} onTicketClosed={handleTicketClosed} />

          {/* Toggle Contact Panel Button - esconde em mobile */}
          {selectedConversation && !showContactPanel && !isMobile && (
            <button
              onClick={() => setShowContactPanel(true)}
              className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-sm transition-all hover:bg-[var(--bg-tertiary)]"
            >
              <User className="h-5 w-5 text-[var(--text-secondary)]" />
            </button>
          )}
        </div>

        {/* Contact Panel - esconde em mobile e tablet (< 1024px) */}
        <AnimatePresence>
          {showContactPanel && !isMobile && !isTablet && (
            <ContactPanel
              conversation={selectedConversation}
              isOpen={showContactPanel}
              onClose={() => setShowContactPanel(false)}
            />
          )}
        </AnimatePresence>

        {/* Contact Panel como Drawer/Overlay para tablet e mobile */}
        <AnimatePresence>
          {showContactPanel && (isMobile || isTablet) && selectedConversation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowContactPanel(false)}
            >
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="absolute right-0 top-0 h-full w-[320px] max-w-[85vw]"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <ContactPanel
                  conversation={selectedConversation}
                  isOpen={showContactPanel}
                  onClose={() => setShowContactPanel(false)}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}